import * as React from 'react';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {Grid} from '@material-ui/core';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {useFormik} from 'formik';
import * as actions from '../../store/actions';
import {
    ImportDevicePhoneBookEntriesDialogProps,
    useStyles,
} from './ImportDevicePhoneBookEntriesDialog.utils';
import {APIErrorInterface, ReduxState} from '../../store/types';
import PermissionProvider from '../../components/PermissionProvider/PermissionProvider';
import {Permission} from '../../store/types/Permission';
import DialogContainer, {DialogButton} from '../../components/AlertDialog/DialogContainer';
import Loader from '../../components/Loader/Loader';
import { showToast } from '../../utils/showToast';
import { CloudUpload } from '@material-ui/icons';
import {useDropzone} from 'react-dropzone';
import { UploadDevicePhoneBookCsvRequest } from '../../store/types/Devices';
import { getReadableSize } from '../Devices/UploadDeviceFileDialog.utils';
import Button from '../../components/Button/Button';

const ImportDevicePhoneBookEntriesDialog: React.FC<ImportDevicePhoneBookEntriesDialogProps> = ({
        directory,
        isOpen,
        clickOnClose,
    }) => {
    const {t} = useTranslation();
    const classes = useStyles();
    const dispatch = useDispatch();
    const [uploadStep, setUploadStep] = useState<number>(1);

    const {
        isUpdatingPhoneBook
    } = useSelector((state: ReduxState) => state.devices);

    const isLoading: boolean = useMemo(
        () => isUpdatingPhoneBook || false,
        [isUpdatingPhoneBook],
    );

    const initialValues = useMemo(() => {
        return {
            name: directory?.name ?? ''
        } as UploadDevicePhoneBookCsvRequest;
    }, [directory]);

    const submitFunc = useCallback((form: UploadDevicePhoneBookCsvRequest) => {
        if(!directory) {
            return;
        }

        const displaySuccessMessageAndNavigate = () => {
            clickOnClose?.(true);
            showToast(t<string>('screens:devicePhoneBooks.devicePhoneBookAdded'));
        };

        const displayUnsuccessMessageAndNavigate = (error: APIErrorInterface) => {
            clickOnClose?.(false);
            showToast(error.faultstring);
        };
        const uploadFileModel = {
                ...form, 
                i_ua_config_directory: directory.i_ua_config_directory,
                callback: displaySuccessMessageAndNavigate,
                errorCallback: displayUnsuccessMessageAndNavigate,
            };
        dispatch(actions.uploadDevicePhoneBookCsvFile.request(uploadFileModel));

    }, [directory]);

    const {
        values,
        handleSubmit,
        resetForm,
        setFieldValue,
        dirty
    } = useFormik<UploadDevicePhoneBookCsvRequest>({
        initialValues,
        onSubmit: submitFunc,
        enableReinitialize: true,
        validateOnChange: false,
        validateOnBlur: true
    });

    useEffect(() => {
        if (!isOpen) {
            resetForm();
            setUploadStep(1);
        }
    }, [isOpen]);

    const isDisabled = (): boolean => {
        return (!dirty || isLoading);
    }

    const onDrop = useCallback((acceptedFiles: File[]) => {
        const file = acceptedFiles && acceptedFiles.length ? acceptedFiles[0] : undefined;
        if (file) {
            setFieldValue('mode', 'csv');
            setFieldValue('fileSizeReadable', getReadableSize(file.size));
            setFieldValue('file', file);
            setFieldValue('fileName', file.name);
            setUploadStep(2);
        }
    }, []);
    
    const {getRootProps, getInputProps} = useDropzone({
        onDrop,
        accept: {'text/csv': ['.csv']},
        disabled: false
    });

    return (
        <PermissionProvider permission={Permission.Inventory.DeviceProfiles.value}>
            <DialogContainer
                className={classes.modalContainer}
                isOpen={isOpen}
                dataQa={'device-phone-book-upload-file-modal'}
                header={t('screens:devicePhoneBooks.importEntriesFromFileDialogTitle')}
                headerClass={classes.header}
                dialogActionsButtons={[
                    <DialogButton
                        key="cancel"
                        label={t('common:cancel')}
                        onClick={() => {
                            clickOnClose?.(false);
                        }}
                    />,
                    <DialogButton
                        key="save"
                        label={t('common:save')}
                        disabled={
                            isDisabled()
                        }
                        onClick={handleSubmit}
                        dataQa={'upload-file-new-device-phone-book-save-button'}
                        primary
                    />,
                ]}
            >
                <form
                    className={classes.form}
                    autoComplete="off"
                    data-testid="upload-file-new-device-phone-book-form"
                >
                    <Grid className={classes.itemsContainer}>
                        <div className={classes.fileDragAndDropBox} {...getRootProps()} data-testid="file-drop-zone">
                            <div className={classes.uploadBox}>
                                <div>
                                    <CloudUpload className={classes.uploadIcon}/>
                                </div>
                                <div className={classes.textWrapper}>
                                    <div className={classes.importTop}>
                                        {uploadStep === 1
                                            ? t('screens:devicePhoneBooks.importDevicesSelectFile')
                                            : values.fileName
                                        }
                                    </div>
                                    <div className={classes.importBottom}>
                                        {uploadStep === 1
                                            ? t('screens:devicePhoneBooks.importDevicesSelectFileSubTitle')
                                            : values.fileSizeReadable
                                        }
                                    </div>
                                </div>
                                <div className={classes.lastColumn}>
                                    <Button
                                        dataQa="browse-button"
                                        primary
                                        accent
                                        className={classes.browseButton}
                                        onClick={(e) => {
                                            e.preventDefault();
                                        }}
                                    >
                                        {t('screens:devicePhoneBooks.browse')}
                                    </Button>
                                </div>
                            </div>
                            <input {...getInputProps()} data-testid='file-upload-input'/>
                        </div>
                    </Grid>
                </form>
                {isOpen && isLoading && (
                    <Loader dataQa={'wait-for-data'} absolutePosition/>
                )}
            </DialogContainer>
        </PermissionProvider>
    );
};

export default ImportDevicePhoneBookEntriesDialog;
